import { z } from 'zod';

export const ACADEMY_PACK_APPLICATION_NAME_PREFIX = 'AIG Bank';
export const AIGBANK_NO_PACK_APPLICATION_NAME_PREFIX = 'AIGBank No Pack';

export const AIGBANK_TESTING_BOX = {
  WHITE_BOX: 'white-box',
  GRAY_BOX: 'gray-box',
} as const;

export const AigbankTestingBoxSchema = z.enum([
  AIGBANK_TESTING_BOX.WHITE_BOX,
  AIGBANK_TESTING_BOX.GRAY_BOX,
]);

export type AigbankTestingBox = z.infer<typeof AigbankTestingBoxSchema>;

export const ProvisionAigbankApplicationBodySchema = z
  .object({
    tenantId: z.string().min(1),
    applicationUrl: z.url(),
    testingBox: AigbankTestingBoxSchema,
    useAcademyPack: z.boolean(),
    includeDomains: z.boolean(),
    includeCredentials: z.boolean(),
    includeZip: z.boolean(),
    includeDocumentation: z.boolean(),
    applicationNameSuffix: z.string().max(80).optional(),
  })
  .superRefine((body, context) => {
    if (
      body.testingBox === AIGBANK_TESTING_BOX.GRAY_BOX &&
      body.useAcademyPack
    ) {
      context.addIssue({
        code: 'custom',
        path: ['useAcademyPack'],
        message: 'Gray-box testing cannot use the academy S3 pack',
      });
    }

    if (body.testingBox === AIGBANK_TESTING_BOX.GRAY_BOX && body.includeZip) {
      context.addIssue({
        code: 'custom',
        path: ['includeZip'],
        message: 'Gray-box testing cannot upload source-code ZIP',
      });
    }

    if (!body.useAcademyPack) {
      return;
    }

    const suffix = body.applicationNameSuffix?.trim() ?? '';

    if (suffix.length === 0) {
      context.addIssue({
        code: 'custom',
        path: ['applicationNameSuffix'],
        message:
          'Academy S3 pack requires an application name suffix (name must be "AIG Bank - …")',
      });
    }
  });

export const ProvisionAigbankApplicationResponseSchema = z.object({
  applicationId: z.string().min(1),
  applicationName: z.string().min(1),
  academyFeatureFlagEnabled: z.boolean(),
  includedDomains: z.array(z.string()),
  credentialsCreated: z.number().int().nonnegative(),
  rolesCreated: z.number().int().nonnegative(),
  zipBound: z.boolean(),
  documentationUploaded: z.boolean(),
});

export type ProvisionAigbankApplicationBody = z.infer<
  typeof ProvisionAigbankApplicationBodySchema
>;
export type ProvisionAigbankApplicationResponse = z.infer<
  typeof ProvisionAigbankApplicationResponseSchema
>;
